import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { mainSiteUrl } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

interface Section {
  h: string;
  p: string[];
}

interface FaqItem {
  q: string;
  a: string;
}

function FaqList({ items }: { items: FaqItem[] }) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <div className="flex flex-col gap-3">
      {items.map((item, idx) => {
        const isOpen = open === idx;
        return (
          <div
            key={idx}
            className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
          >
            <button
              onClick={() => setOpen(isOpen ? null : idx)}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
            >
              <span className="font-semibold text-foreground-900">{item.q}</span>
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-600">
                <i
                  className={`${isOpen ? "ri-subtract-line" : "ri-add-line"} w-4 h-4 flex items-center justify-center`}
                />
              </span>
            </button>
            {isOpen && (
              <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600">
                {item.a}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Article() {
  const { slug } = useParams();
  const { t } = useTranslation();

  const meta = articles.find((a) => a.slug === slug);
  const hasContent = !!meta;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!hasContent || !slug) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-4 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950">
            {t("article.notFound", "Article not found")}
          </h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line w-4 h-4 flex items-center justify-center" />
            {t("backHome")}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const title = t(`articles.${slug}.title`);
  const sections = t(`articles.${slug}.sections`, { returnObjects: true }) as unknown as Section[];
  const faqItems = t(`articles.${slug}.faq`, { returnObjects: true }) as unknown as FaqItem[];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Article hero */}
      <section className="relative flex items-center justify-center overflow-hidden">
        <img
          src={meta.image}
          alt={title}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/55" />
        <div className="relative z-10 w-full px-4 py-20 text-center md:px-6 md:py-28">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur">
            {t(`articles.${slug}.tag`)}
          </span>
          <h1 className="mx-auto mt-5 max-w-4xl font-heading text-3xl font-bold leading-tight text-white md:text-5xl">
            {title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-white/85">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line w-4 h-4 flex items-center justify-center" />
              {t("updated")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line w-4 h-4 flex items-center justify-center" />
              {t(`articles.${slug}.readingTime`)}
            </span>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
        {/* Breadcrumb */}
        <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">
            {t("breadcrumbHome")}
          </Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <Link to="/#guides" className="transition-colors hover:text-primary-600">
            {t("nav.guides")}
          </Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <span className="truncate text-foreground-700">{title}</span>
        </nav>

        {/* Lead */}
        <p className="mt-8 text-lg leading-relaxed text-foreground-700">
          {t(`articles.${slug}.lead`)}
        </p>

        {/* Sections */}
        <article className="mt-8 flex flex-col gap-10">
          {sections.map((section, idx) => (
            <section key={idx}>
              <h2 className="font-heading text-2xl font-bold text-foreground-950">
                {section.h}
              </h2>
              {section.p.map((para, pIdx) => (
                <p key={pIdx} className="mt-4 leading-relaxed text-foreground-700">
                  {para}
                </p>
              ))}
            </section>
          ))}
        </article>

        {/* Internal link to homepage categories */}
        <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5">
          <p className="text-sm leading-relaxed text-foreground-700">
            {t("article.browseHint", "Looking for specific products? Browse the full category list on our homepage.")}{" "}
            <Link
              to="/#categories"
              className="font-semibold text-primary-600 transition-colors hover:text-primary-700"
            >
              {t("categories.title")}
            </Link>
          </p>
        </div>

        {/* FAQ */}
        <section className="mt-12">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">
            {t("faq.title")}
          </h2>
          <div className="mt-5">
            <FaqList items={faqItems} />
          </div>
        </section>

        {/* CTA */}
        <section className="mt-12 rounded-lg bg-primary-600 p-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-white">
            {t("cta.title")}
          </h2>
          <p className="mx-auto mt-2 max-w-md text-primary-50">
            {t("cta.subtitle")}
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 rounded-full bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 whitespace-nowrap transition-colors hover:bg-white"
            >
              <i className="ri-external-link-line w-4 h-4 flex items-center justify-center" />
              {t("cta.button")}
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3 text-sm font-semibold text-white whitespace-nowrap transition-colors hover:bg-white/10"
            >
              <i className="ri-home-4-line w-4 h-4 flex items-center justify-center" />
              {t("backHome")}
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}